import { formatDateTime as formatWithSettings } from '../../utils/dateFormat';

export const parseEventDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

export const formatDateTime = (value) => {
  const date = parseEventDate(value);
  if (!date) return 'Unknown date';
  return formatWithSettings(date);
};

export const normalizeSeverity = (severity) => {
  const safe = (severity || 'low').toLowerCase();
  if (safe === 'critical' || safe === 'high') return 'critical';
  if (safe === 'medium') return 'medium';
  return 'low';
};

export const parseAttendanceDescription = (description = '') => {
  if (!description) return { status: '', programme: '', notes: '' };
  if (description.startsWith('Absence:')) {
    return { status: 'authorised absence', programme: '', notes: description.replace('Absence:', '').trim() };
  }
  if (!description.startsWith('Session:')) {
    return { status: '', programme: '', notes: description };
  }
  const after = description.replace('Session:', '').trim();
  const parts = after.split(' - ');
  const statusPart = parts[0];
  const notes = parts.slice(1).join(' - ').trim();
  const programmeMatch = statusPart.match(/\(([^)]+)\)/);
  const programme = programmeMatch ? programmeMatch[1].trim() : '';
  const status = statusPart.replace(/\(.*\)/, '').trim();
  return { status, programme, notes };
};

export const isAttendedSession = (event) => {
  if (event.category !== 'Attendance') return false;
  const { status } = parseAttendanceDescription(event.description);
  return status.toLowerCase() === 'attended';
};

export const isAbsence = (event) => {
  if (event.category !== 'Attendance') return false;
  const { status } = parseAttendanceDescription(event.description);
  return status.toLowerCase().includes('absence');
};

export const isUnauthorisedAbsence = (event) => {
  if (event.category !== 'Attendance') return false;
  const { status } = parseAttendanceDescription(event.description);
  return status.toLowerCase().includes('unauthorised') || status.toLowerCase().includes('unauthorized');
};

export const buildTimelineEntries = (events) => {
  return (events || []).map((event) => {
    const dateObj = parseEventDate(event.date);
    const attendanceMeta = event.category === 'Attendance' ? parseAttendanceDescription(event.description) : null;
    return {
      ...event,
      dateObj,
      risk: normalizeSeverity(event.severity),
      attendanceMeta
    };
  });
};

export const groupEventsByTime = (events, now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const weekStart = new Date(today);
  const day = weekStart.getDay();
  const diff = (day + 6) % 7;
  weekStart.setDate(weekStart.getDate() - diff);

  const groups = {
    today: [],
    week: [],
    earlier: []
  };

  events.forEach((event) => {
    if (!event.dateObj) return;
    const eventDate = new Date(event.dateObj);
    const eventDay = new Date(eventDate);
    eventDay.setHours(0, 0, 0, 0);
    if (eventDay.getTime() === today.getTime()) {
      groups.today.push(event);
      return;
    }
    if (eventDay >= weekStart) {
      groups.week.push(event);
      return;
    }
    groups.earlier.push(event);
  });

  return groups;
};

export const clusterAttendance = (events) => {
  const attendanceMap = new Map();
  const remainder = [];

  events.forEach((event) => {
    if (!isAttendedSession(event)) {
      remainder.push(event);
      return;
    }
    const programme = event.attendanceMeta?.programme || 'General';
    const key = `${programme}`;
    if (!attendanceMap.has(key)) {
      attendanceMap.set(key, []);
    }
    attendanceMap.get(key).push(event);
  });

  const clusters = Array.from(attendanceMap.entries()).map(([programme, items]) => {
    const latest = items.reduce((acc, item) => (item.dateObj > acc ? item.dateObj : acc), items[0].dateObj);
    return {
      id: `cluster-${programme}-${items.length}`,
      type: 'attendance-cluster',
      programme,
      count: items.length,
      items,
      dateObj: latest,
      risk: 'low'
    };
  });

  const combined = [...remainder, ...clusters];
  return combined.sort((a, b) => {
    const aDate = a.dateObj || a.items?.[0]?.dateObj || 0;
    const bDate = b.dateObj || b.items?.[0]?.dateObj || 0;
    return bDate - aDate;
  });
};

export const buildSafeguardingSignals = (events, now = new Date()) => {
  const signals = [];
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - 14);
  const unauthorised = events.filter((event) => {
    if (!event.dateObj) return false;
    return event.dateObj >= cutoff && isUnauthorisedAbsence(event);
  });

  if (unauthorised.length >= 3) {
    signals.push({
      id: 'signal-unauth-14',
      label: `${unauthorised.length} unauthorised absences in 14 days`,
      risk: unauthorised.length >= 5 ? 'critical' : 'medium',
      dateObj: now
    });
  }

  return signals;
};
